<template>
  <!-- One logical page that exists separately for several regions. The list
       shows it once; the region chips lead to each region's own page, which
       can be built completely differently. -->
  <div class="item group-item">
    <span class="dot" :class="groupDotClass" :title="groupDotTitle"></span>
    <router-link :to="'/editor/' + group.main.id" class="page-name">
      {{ group.name }}
    </router-link>
    <button
      class="badge-soft badge-blue category-tag"
      v-if="category"
      title="Filter by this category"
      v-on:click="$emit('filter-category', category)"
    >
      {{ category }}
    </button>
    <span class="region-chips">
      <router-link
        v-for="variant in group.variants"
        :key="variant.id"
        :to="'/editor/' + variant.id"
        class="region-chip"
        :class="'is-' + variantState(variant)"
        :title="
          regionLabel(variant) +
          ' — ' +
          stateWord(variant) +
          ' · click to edit this region'
        "
      >
        {{ regionLabel(variant) }}
      </router-link>
    </span>
    <!-- Sits where every other row keeps its buttons, so the row still starts
         at the status dot and the whole list stays in one column. -->
    <button
      class="icon-btn group-toggle"
      :class="{ open: expanded }"
      :title="
        expanded
          ? 'Hide the regions'
          : 'Show the ' + group.variants.length + ' regions'
      "
      v-on:click="$emit('toggle')"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path
          d="M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z"
        />
      </svg>
    </button>
  </div>

  <div class="variant-rows" v-if="expanded">
    <div
      class="item variant-row"
      v-for="variant in group.variants"
      :key="variant.id"
    >
      <span
        class="dot"
        :class="variantDotClass(variant)"
        :title="stateWord(variant)"
      ></span>
      <router-link :to="'/editor/' + variant.id" class="page-name">
        <span class="variant-region">{{ regionLabel(variant) }}</span>
        <span class="variant-name">{{ variant.name }}</span>
      </router-link>
      <a
        class="icon-btn icon-btn-accent"
        v-if="variant.is_active"
        :href="liveUrl(variant)"
        target="_blank"
        title="Open live page"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M10 6v2H5v11h11v-5h2v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6zm11-3v8h-2V6.413l-7.793 7.794-1.414-1.414L17.585 5H13V3h8z"
          />
        </svg>
      </a>
      <button
        class="btn btn-primary btn-small"
        v-if="variantState(variant) === 'draft'"
        v-on:click="$emit('activate', variant)"
      >
        Activate
      </button>
      <button
        class="icon-btn icon-btn-accent"
        title="Page settings"
        v-on:click="$emit('settings', variant)"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M2 12c0-.865.11-1.703.316-2.504A3 3 0 0 0 4.99 4.867a9.99 9.99 0 0 1 4.335-2.505 3 3 0 0 0 5.348 0 9.99 9.99 0 0 1 4.335 2.505 3 3 0 0 0 2.675 4.63c.206.8.316 1.638.316 2.503 0 .865-.11 1.703-.316 2.504a3 3 0 0 0-2.675 4.629 9.99 9.99 0 0 1-4.335 2.505 3 3 0 0 0-5.348 0 9.99 9.99 0 0 1-4.335-2.505 3 3 0 0 0-2.675-4.63C2.11 13.704 2 12.866 2 12zm4.804 3c.63 1.091.81 2.346.564 3.524.408.29.842.541 1.297.75A4.993 4.993 0 0 1 12 18c1.26 0 2.438.471 3.335 1.274.455-.209.889-.46 1.297-.75A4.993 4.993 0 0 1 17.196 15a4.993 4.993 0 0 1 2.77-2.25 8.126 8.126 0 0 0 0-1.5A4.993 4.993 0 0 1 17.195 9a4.993 4.993 0 0 1-.564-3.524 7.989 7.989 0 0 0-1.297-.75A4.993 4.993 0 0 1 12 6a4.993 4.993 0 0 1-3.335-1.274 7.99 7.99 0 0 0-1.297.75A4.993 4.993 0 0 1 6.804 9a4.993 4.993 0 0 1-2.77 2.25 8.126 8.126 0 0 0 0 1.5A4.993 4.993 0 0 1 6.805 15zM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
          />
        </svg>
      </button>
      <button
        class="icon-btn"
        v-if="variant.is_active"
        title="Visible — click to hide this region's page"
        v-on:click="$emit('hide', variant)"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M12 3c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.818-9C2.122 6.88 6.608 3 12 3zm0 16c4.235 0 7.86-2.948 8.777-7C19.86 7.948 16.235 5 12 5c-4.236 0-7.86 2.948-8.777 7C4.14 16.052 7.764 19 12 19zm0-2.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
          />
        </svg>
      </button>
      <button
        class="icon-btn"
        v-if="variantState(variant) === 'hidden'"
        title="Hidden — click to restore as draft"
        v-on:click="$emit('restore', variant)"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M17.882 19.297A10.949 10.949 0 0 1 12 21c-5.392 0-9.878-3.88-10.819-9a10.982 10.982 0 0 1 3.34-6.066L1.392 2.808l1.415-1.415 19.799 19.8-1.415 1.414-3.31-3.31zM5.935 7.35A8.965 8.965 0 0 0 3.223 12a9.005 9.005 0 0 0 13.201 5.838l-2.028-2.028A4.5 4.5 0 0 1 8.19 9.604L5.935 7.35zm6.979 6.978l-3.242-3.242a2.5 2.5 0 0 0 3.241 3.241zm7.893 2.264l-1.431-1.43A8.935 8.935 0 0 0 20.777 12 9.005 9.005 0 0 0 9.552 5.338L7.974 3.76C9.221 3.27 10.58 3 12 3c5.392 0 9.878 3.88 10.819 9a10.947 10.947 0 0 1-2.012 4.592zm-9.084-9.084a4.5 4.5 0 0 1 4.769 4.769l-4.77-4.769z"
          />
        </svg>
      </button>
      <button
        class="icon-btn icon-btn-danger"
        title="Delete this region's page"
        v-on:click="$emit('remove', variant)"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "PageGroupRow",
  props: {
    group: { type: Object, required: true },
    expanded: { type: Boolean, default: false },
    category: { type: String, default: null },
    regionLabel: { type: Function, required: true },
    variantState: { type: Function, required: true },
    liveUrl: { type: Function, required: true },
  },
  emits: [
    "toggle",
    "filter-category",
    "settings",
    "activate",
    "hide",
    "restore",
    "remove",
  ],
  computed: {
    // Green only when every region is live: a half-published page is exactly
    // what this row exists to make visible.
    groupDotClass() {
      if (this.group.activeCount === 0) {
        return this.group.hiddenCount === this.group.variants.length
          ? "dot-red"
          : "";
      }
      return this.group.activeCount === this.group.variants.length
        ? "dot-green"
        : "dot-partial";
    },
    groupDotTitle() {
      const total = this.group.variants.length;
      if (this.group.activeCount === 0) {
        return "No region is live";
      }
      if (this.group.activeCount === total) {
        return "Live in all " + total + " regions";
      }
      return "Live in " + this.group.activeCount + " of " + total + " regions";
    },
  },
  methods: {
    stateWord(variant) {
      const state = this.variantState(variant);
      return state === "live" ? "live" : state;
    },
    variantDotClass(variant) {
      const state = this.variantState(variant);
      if (state === "live") {
        return "dot-green";
      }
      return state === "hidden" ? "dot-red" : "";
    },
  },
};
</script>

<style scoped>
/* Same icon button as every other row action — it just points the other way
   once the regions are open. */
.group-toggle svg {
  display: block;
  transition: transform 0.15s;
}
.group-toggle.open svg {
  transform: rotate(180deg);
}
.page-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.region-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.region-chip {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 2px 7px;
  border-radius: 99px;
  text-decoration: none;
  border: 1px solid transparent;
  background: #f1f5f9;
  color: #94a3b8;
}
.region-chip:hover {
  border-color: #c7d2fe;
}
.region-chip.is-live {
  background: #dcfce7;
  color: #15803d;
}
.region-chip.is-draft {
  background: #fef9c3;
  color: #a16207;
}
.region-chip.is-hidden {
  background: #f1f5f9;
  color: #94a3b8;
  text-decoration: line-through;
}
.dot-partial {
  background: #f59e0b;
}

.variant-rows {
  margin: 0 0 4px 18px;
  border-left: 2px solid #e2e8f0;
  padding-left: 8px;
}
.variant-row {
  padding-top: 5px;
  padding-bottom: 5px;
}
.variant-region {
  display: inline-block;
  min-width: 42px;
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  margin-right: 8px;
}
.variant-name {
  font-size: 13px;
  color: #475569;
}
</style>
